import NavigationArchitectureSidebar from "@/components/NavigationArchitectureSidebar";
import SearchConditionRow from "@/components/SearchConditionRow";
import SearchConditionTag, {
  SearchConditionTagType,
} from "@/components/SearchConditionTag";
import { TabProps } from "@/components/Tab";
import Tabs from "@/components/Tabs";
import { ArchitectureResultTabType } from "@/consts/tabType";
import useArchitectureSearchCondition from "@/hooks/useArchitectureSearchCondition";
import useBuildingTypes from "@/hooks/useBuildingTypes";
import useMaterials from "@/hooks/useMaterials";
import useDisclosure from "@/hooks/useDisclosure";
import { SearchQueryParameterController } from "@/hooks/useSearchQueryParameter";
import { FC, PropsWithChildren, useMemo } from "react";
import { ArchitectureSearchQueryParameter } from "@/types/ArchitectureSearchQueryParameter";
import ModalSearchArchitecture from "@/components/ModalSearchArchitecture";
import SearchArchitectureDropdowns from "@/components/SearchArchitectureDropdowns";
import SearchGroup from "@/components/SearchGroup";

interface Props {
  tabType: ArchitectureResultTabType;
  buildingTypeId?: number;
  amount?: number;
  query: ArchitectureSearchQueryParameter;
  queryController: SearchQueryParameterController<ArchitectureSearchQueryParameter>;
}

const ArchitectureSearchGroup: FC<PropsWithChildren<Props>> = ({
  children,
  tabType,
  buildingTypeId,
  amount,
  query,
  queryController,
}): JSX.Element => {
  const buildingTypes = useBuildingTypes();
  const materials = useMaterials();
  const { conditions, hasCondition } = useArchitectureSearchCondition(
    query,
    buildingTypes,
    materials,
  );

  const forProject = useMemo<boolean>(
    () => tabType === ArchitectureResultTabType.Project,
    [tabType],
  );

  const tabs = useMemo<TabProps[]>(
    () => [
      {
        isCurrent: !forProject,
        to: {
          pathname: "/architectures/posts/search",
          query,
        },
        label: "写真",
      },
      {
        isCurrent: forProject,
        to: {
          pathname: "/architectures/projects/search",
          query,
        },
        label: "プロジェクト",
      },
    ],
    [forProject, query],
  );

  const { openHandler, closeHandler, isOpen } = useDisclosure();

  return (
    <SearchGroup
      hasCondition={hasCondition}
      dropdowns={
        <SearchArchitectureDropdowns
          buildingTypes={buildingTypes}
          query={query}
          queryController={queryController}
          onOpenModal={openHandler}
        />
      }
      modal={
        <ModalSearchArchitecture
          buildingTypes={buildingTypes}
          query={query}
          closeHandler={closeHandler}
        />
      }
      modalOpen={isOpen}
      sidebar={
        <NavigationArchitectureSidebar
          buildingTypeId={buildingTypeId}
          query={query}
          queryController={queryController}
        />
      }
      conditions={
        <SearchConditionRow
          basePath={queryController.basePath}
          visibleClearBtn={hasCondition}
        >
          {conditions.map((condition) => {
            return (
              <SearchConditionTag
                key={condition.label}
                name={condition.label}
                type={SearchConditionTagType.Delete}
                onClick={() => {
                  queryController.deleteQueryAt(condition.key, condition.value);

                  // 建材を削除した時は、建材の色・建材の使用箇所も削除する
                  if (condition.key === "material_id") {
                    queryController.deleteQuery("color_type_ids");
                    queryController.deleteQuery("part_ids");
                  }

                  queryController.submitQuery();
                }}
              />
            );
          })}
        </SearchConditionRow>
      }
    >
      <div className="space-y-12 laptop:space-y-20">
        <Tabs tabs={tabs} amount={amount} />
        {children}
      </div>
    </SearchGroup>
  );
};

export default ArchitectureSearchGroup;
